import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { FileText, CreditCard, ChevronRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/StatusBadge';
import './InvoicesPage.css';

/**
 * Invoices page - Billing and payment management.
 * Staff can create invoices, clients can view/pay their own.
 */
export const InvoicesPage = () => {
  const { user } = useEntitlements();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [clients, setClients] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [formData, setFormData] = useState({
    client: '',
    invoice_number: '',
    description: '',
    tax_rate: '',
    due_date: '',
    amount: '',
    notes: '',
  });
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  useEffect(() => {
    fetchInvoices();
    if (isStaff) {
      fetchClients();
      fetchPaymentMethods();
    }
  }, [isStaff]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/v1/tenant/payment-methods/');
      setPaymentMethods(response.data.filter(method => method.is_enabled));
    } catch (err) {
      console.error('Failed to fetch payment methods:', err);
    }
  };
  
  useEffect(() => {
    // Calculate preview invoice number when form changes
    if (!formData.invoice_number) {
      const year = new Date().getFullYear();
      setPreviewInvoiceNumber(`INV-${year}-XXXX (auto-generated)`);
    } else {
      setPreviewInvoiceNumber(formData.invoice_number);
    }
  }, [formData.invoice_number]);

  // Calculate preview amounts when subtotal or tax rate changes
  const calculatePreviewAmounts = () => {
    const subtotal = parseFloat(formData.amount) || 0;
    const taxRate = parseFloat(formData.tax_rate) || 0;

    // Calculate tax amount and round to 2 decimal places
    const taxAmount = Math.round((subtotal * taxRate) / 100 * 100) / 100;

    // Add rounded tax to subtotal
    const totalAmount = subtotal + taxAmount;

    return {
      subtotal: subtotal.toFixed(2),
      taxRate: taxRate.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
    };
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/invoices/');
      setInvoices(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/v1/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      // Prepare invoice data - backend expects 'amount' which it syncs to 'subtotal_amount'
      const invoiceData = {
        client: formData.client,
        due_date: formData.due_date,
        amount: parseFloat(formData.amount) || 0,
      };
      
      // Optional fields
      if (formData.invoice_number) {
        invoiceData.invoice_number = formData.invoice_number;
      }
      if (formData.description) {
        invoiceData.description = formData.description;
      }
      if (formData.tax_rate) {
        invoiceData.tax_rate = parseFloat(formData.tax_rate) || 0;
      }
      if (formData.notes) {
        invoiceData.notes = formData.notes;
      }
      
      // Payment methods (required when sending invoice)
      if (selectedPaymentMethods.length > 0) {
        invoiceData.enabled_payment_methods_ids = selectedPaymentMethods;
      }
      
      await api.post('/v1/invoices/', invoiceData);
      setShowCreateModal(false);
      setFormData({
        client: '',
        invoice_number: '',
        description: '',
        tax_rate: '',
        due_date: '',
        amount: '',
        notes: '',
      });
      setPreviewInvoiceNumber('');
      setError(null);
      fetchInvoices();
    } catch (err) {
      // Enhanced error handling
      let errorMsg = 'Failed to create invoice';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else {
          const errors = [];
          for (const [field, messages] of Object.entries(err.response.data)) {
            if (Array.isArray(messages)) {
              errors.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errors.push(`${field}: ${messages}`);
            }
          }
          if (errors.length > 0) {
            errorMsg = errors.join(' | ');
          }
        }
      }
      setError(errorMsg);
    }
  };

  const handleEditInvoice = (invoice) => {
    // Format dates for date inputs (YYYY-MM-DD)
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    setEditingInvoice(invoice);
    setFormData({
      client: invoice.client || '',
      invoice_number: invoice.invoice_number || '',
      description: invoice.description || '',
      tax_rate: invoice.tax_rate || '',
      due_date: formatDate(invoice.due_date) || '',
      amount: invoice.subtotal_amount || invoice.amount || '',
      notes: invoice.notes || '',
    });
    // Set selected payment methods from invoice
    if (invoice.enabled_payment_methods && invoice.enabled_payment_methods.length > 0) {
      setSelectedPaymentMethods(invoice.enabled_payment_methods.map(method => method.id));
    } else {
      setSelectedPaymentMethods([]);
    }
    setPreviewInvoiceNumber(invoice.invoice_number || '');
    setShowEditModal(true);
    setError(null);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;

    try {
      // Prepare invoice data - backend expects 'amount' which it syncs to 'subtotal_amount'
      const invoiceData = {
        client: formData.client,
        due_date: formData.due_date,
        amount: parseFloat(formData.amount) || 0,
      };
      
      // Optional fields
      if (formData.description) {
        invoiceData.description = formData.description;
      }
      if (formData.tax_rate) {
        invoiceData.tax_rate = parseFloat(formData.tax_rate) || 0;
      }
      if (formData.notes) {
        invoiceData.notes = formData.notes;
      }
      
      // Payment methods
      if (selectedPaymentMethods.length > 0) {
        invoiceData.enabled_payment_methods_ids = selectedPaymentMethods;
      }
      
      await api.patch(`/v1/invoices/${editingInvoice.id}/`, invoiceData);
      setShowEditModal(false);
      setEditingInvoice(null);
      setFormData({
        client: '',
        invoice_number: '',
        description: '',
        tax_rate: '',
        due_date: '',
        amount: '',
        notes: '',
      });
      setPreviewInvoiceNumber('');
      setError(null);
      fetchInvoices();
    } catch (err) {
      // Enhanced error handling
      let errorMsg = 'Failed to update invoice';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else {
          const errors = [];
          for (const [field, messages] of Object.entries(err.response.data)) {
            if (Array.isArray(messages)) {
              errors.push(`${field}: ${messages.join(', ')}`);
            } else if (typeof messages === 'string') {
              errors.push(`${field}: ${messages}`);
            }
          }
          if (errors.length > 0) {
            errorMsg = errors.join(' | ');
          }
        }
      }
      setError(errorMsg);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/v1/invoices/${invoiceId}/send_invoice/`);
      setError(null);
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const response = await api.post(`/v1/invoices/${invoiceId}/pay/`);
      // In a real implementation, you would redirect to Stripe Checkout
      // or use Stripe Elements to collect payment
      alert(`Payment initiated. Payment Intent ID: ${response.data.payment_intent_id}`);
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusVariant = (status) => {
    const map = {
      draft: 'neutral',
      sent: 'accent',
      paid: 'success',
      overdue: 'danger',
      cancelled: 'neutral',
      refunded: 'warning',
      void: 'neutral',
    };
    return map[status] || 'neutral';
  };

  // Filter invoices based on selected filters
  const filteredInvoices = invoices.filter((invoice) => {
    // Filter by status
    if (filterStatus && invoice.status !== filterStatus) {
      return false;
    }
    
    // Filter by client (only for staff)
    // Compare as strings since IDs might be UUIDs or strings
    if (isStaff && filterClient && String(invoice.client) !== String(filterClient)) {
      return false;
    }
    
    return true;
  });

  if (loading) {
    return <div className="invoices-loading">Loading invoices...</div>;
  }

  return (
    <div className="invoices-page">
      <PageHeader
        icon={FileText}
        title="Invoices"
        subtitle="Track billing status, payments, and receivables."
        primaryAction={isStaff ? (
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            <CreditCard size={16} />
            Create Invoice
          </button>
        ) : null}
      />

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="invoices-filters">
        <div className="filter-group">
          <label htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="void">Void</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
        {isStaff && (
          <div className="filter-group">
            <label htmlFor="filter-client">Client</label>
            <select
              id="filter-client"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {(filterStatus || filterClient) && (
          <button
            className="btn btn-sm"
            onClick={() => {
              setFilterStatus('');
              setFilterClient('');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="invoices-list">
        {filteredInvoices.length === 0 ? (
          <div className="portalr-emptyState">
            <div className="portalr-emptyStateIcon" aria-hidden="true">
              <CreditCard size={20} />
            </div>
            <div className="portalr-emptyStateTitle">
              {invoices.length === 0 ? 'No invoices yet' : 'No invoices match your filters'}
            </div>
            <p className="portalr-emptyStateDesc">
              {invoices.length === 0
                ? 'Create your first invoice and start getting paid.'
                : 'Try adjusting status or client filters to see more results.'}
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {isStaff && invoices.length === 0 ? (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create invoice
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className="portalr-btn portalr-btn--secondary"
                  onClick={() => {
                    setFilterStatus('');
                    setFilterClient('');
                  }}
                >
                  Clear filters
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredInvoices.map((invoice) => (
            <div key={invoice.id} className="invoice-card">
              <div className="invoice-header">
                <div>
                  <h3>Invoice {invoice.invoice_number}</h3>
                  {invoice.client_name && (
                    <p className="invoice-client">Client: {invoice.client_name}</p>
                  )}
                </div>
                <StatusBadge
                  className="invoice-status"
                  label={invoice.status}
                  variant={getStatusVariant(invoice.status)}
                />
              </div>
              {invoice.description && (
                <p className="invoice-description">{invoice.description}</p>
              )}
              <div className="invoice-amounts">
                <div className="invoice-amount-row">
                  <span>Subtotal:</span>
                  <strong>{formatCurrency(invoice.subtotal_amount || invoice.amount)}</strong>
                </div>
                {invoice.tax_rate > 0 && (
                  <div className="invoice-amount-row">
                    <span>Tax ({invoice.tax_rate}%):</span>
                    <strong>{formatCurrency(invoice.tax_amount)}</strong>
                  </div>
                )}
                {invoice.tax_amount > 0 && invoice.tax_rate === 0 && (
                  <div className="invoice-amount-row">
                    <span>Tax:</span>
                    <strong>{formatCurrency(invoice.tax_amount)}</strong>
                  </div>
                )}
                <div className="invoice-amount-row invoice-total">
                  <span>Total:</span>
                  <strong>{formatCurrency(invoice.total_amount)}</strong>
                </div>
              </div>
              <div className="invoice-meta">
                {invoice.issue_date && (
                  <span>Issue Date: {new Date(invoice.issue_date).toLocaleDateString()}</span>
                )}
                {invoice.due_date && (
                  <span className={invoice.is_overdue ? 'overdue' : ''}>
                    Due Date: {new Date(invoice.due_date).toLocaleDateString()}
                  </span>
                )}
                {invoice.paid_at && (
                  <span>Paid: {new Date(invoice.paid_at).toLocaleDateString()}</span>
                )}
              </div>
              {/* Payment Methods Display (for clients) */}
              {!isStaff && invoice.enabled_payment_methods && invoice.enabled_payment_methods.length > 0 && invoice.status !== 'paid' && (
                <div className="invoice-payment-methods">
                  <h4>Payment Options</h4>
                  {invoice.enabled_payment_methods.map((method) => (
                    <div key={method.id} className="payment-method-option">
                      <div className="payment-method-option-header">
                        <h5>{method.display_name}</h5>
                        <span className="payment-method-type-badge">{method.type}</span>
                      </div>
                      {method.type === 'stripe' ? (
                        <div className="payment-method-option-actions">
                          <button
                            className="btn btn-primary"
                            onClick={() => handlePayInvoice(invoice.id)}
                          >
                            Pay Now with Stripe
                          </button>
                        </div>
                      ) : (
                        <>
                          {method.instructions && (
                            <div className="payment-method-option-instructions">
                              {method.instructions}
                            </div>
                          )}
                          {method.external_reference && (
                            <div className="payment-method-option-reference">
                              <strong>Reference:</strong> {method.external_reference}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="invoice-actions">
                {invoice.status === 'sent' && !isStaff && invoice.enabled_payment_methods && invoice.enabled_payment_methods.some(m => m.type === 'stripe') && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handlePayInvoice(invoice.id)}
                  >
                    Pay Invoice
                  </button>
                )}
                {isStaff && invoice.status === 'draft' && (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleEditInvoice(invoice)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleSendInvoice(invoice.id)}
                    >
                      Send Invoice
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {(showCreateModal || showEditModal) && (
        <div 
          className="modal-overlay" 
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setEditingInvoice(null);
            setFormData({
              client: '',
              invoice_number: '',
              description: '',
              tax_rate: '',
              due_date: '',
              amount: '',
              notes: '',
            });
            setSelectedPaymentMethods([]);
            setPreviewInvoiceNumber('');
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{showEditModal ? 'Edit Invoice' : 'Create Invoice'}</h2>
            <form onSubmit={showEditModal ? handleUpdateInvoice : handleCreateInvoice}>
              <div className="form-group">
                <label>Client Name *</label>
                <select
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  required
                  disabled={!isStaff}
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="Leave blank to auto-generate"
                  disabled={showEditModal}
                />
                {previewInvoiceNumber && (
                  <small className="form-help">
                    {showEditModal ? 'Invoice number cannot be changed' : `Preview: ${previewInvoiceNumber}`}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Short summary of invoice"
                />
              </div>
              <div className="form-group">
                <label>Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                  placeholder="e.g., 8.5 for 8.5%"
                />
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Subtotal Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
                <small className="form-help">
                  Amount before tax. Tax will be calculated automatically if tax rate is provided.
                </small>
              </div>

              {/* Invoice Amount Preview */}
              {(formData.amount || formData.tax_rate) && (
                <div className="form-group invoice-preview">
                  <label>Invoice Total Preview</label>
                  <div className="invoice-preview-amounts">
                    <div className="preview-row">
                      <span>Subtotal:</span>
                      <strong>{formatCurrency(parseFloat(calculatePreviewAmounts().subtotal) || 0)}</strong>
                    </div>
                    {parseFloat(formData.tax_rate) > 0 && (
                      <div className="preview-row">
                        <span>Tax ({calculatePreviewAmounts().taxRate}%):</span>
                        <strong>{formatCurrency(parseFloat(calculatePreviewAmounts().taxAmount) || 0)}</strong>
                      </div>
                    )}
                    <div className="preview-row preview-total">
                      <span>Total:</span>
                      <strong>{formatCurrency(parseFloat(calculatePreviewAmounts().totalAmount) || 0)}</strong>
                    </div>
                  </div>
                  <small className="form-help">
                    This is a preview of how the invoice will appear to the client.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  placeholder="Internal notes (not visible to client)"
                />
                <small className="form-help">
                  Internal notes for staff reference only
                </small>
              </div>

              {/* Payment Methods Section */}
              {isStaff && (
                <div className="form-group">
                  <label>Payment Methods *</label>
                  <div className="payment-methods-selection">
                    {paymentMethods.length === 0 ? (
                      <div className="payment-methods-empty">
                        <p>No payment methods configured. <a href="/settings?tab=payment-methods">Add payment methods</a> first.</p>
                      </div>
                    ) : (
                      <>
                        {paymentMethods.map((method) => (
                          <label key={method.id} className="payment-method-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedPaymentMethods.includes(method.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedPaymentMethods([...selectedPaymentMethods, method.id]);
                                } else {
                                  setSelectedPaymentMethods(selectedPaymentMethods.filter(id => id !== method.id));
                                }
                              }}
                            />
                            <div className="payment-method-checkbox-content">
                              <strong>{method.display_name}</strong>
                              <span className="payment-method-type-badge">{method.type}</span>
                            </div>
                          </label>
                        ))}
                        {selectedPaymentMethods.length === 0 && (
                          <small className="form-help form-error">
                            At least one payment method must be selected when sending the invoice.
                          </small>
                        )}
                        <small className="form-help">
                          Select one or more payment methods that clients can use to pay this invoice.
                        </small>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setEditingInvoice(null);
                    setFormData({
                      client: '',
                      invoice_number: '',
                      description: '',
                      tax_rate: '',
                      due_date: '',
                      amount: '',
                      notes: '',
                    });
                    setSelectedPaymentMethods([]);
                    setPreviewInvoiceNumber('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {showEditModal ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

